import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  SaveAlt as SaveTemplateIcon,
} from '@mui/icons-material';
import type { CreateVorlageDialogProps } from './types';
import { WORKFLOW_KATEGORIEN } from './utils';
import WorkflowStepEditor from './WorkflowStepEditor';

const CreateVorlageDialog: React.FC<CreateVorlageDialogProps> = ({
  open,
  onClose,
  formData,
  onFormChange,
  onAddStep,
  onRemoveStep,
  onStepChange,
  onCreate,
  isCreating,
  availableUsers,
  availableWebhooks,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SaveTemplateIcon color="primary" />
          Neue Workflow-Vorlage erstellen
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
          Workflow-Vorlagen sind permanente Templates, die Benutzer immer wieder abrufen und starten können.
        </Alert>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Vorlagen-Name"
              value={formData.name}
              onChange={(e) => onFormChange({ ...formData, name: e.target.value })}
              required
              placeholder="z.B. Standard-Onboarding"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Kategorie</InputLabel>
              <Select
                value={formData.kategorie}
                onChange={(e) => onFormChange({ ...formData, kategorie: e.target.value })}
                label="Kategorie"
              >
                {WORKFLOW_KATEGORIEN.map((kat) => (
                  <MenuItem key={kat} value={kat}>{kat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Beschreibung (optional)"
              value={formData.beschreibung}
              onChange={(e) => onFormChange({ ...formData, beschreibung: e.target.value })}
              placeholder="Beschreiben Sie, wofür diese Vorlage gedacht ist..."
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          Workflow-Schritte
        </Typography>

        {formData.schritte.map((schritt, index) => (
          <WorkflowStepEditor
            key={index}
            schritt={schritt}
            index={index}
            canRemove={formData.schritte.length > 1}
            onRemove={onRemoveStep}
            onChange={onStepChange}
            showAutomation
            availableUsers={availableUsers}
            availableWebhooks={availableWebhooks}
          />
        ))}

        <Button startIcon={<AddIcon />} onClick={onAddStep} variant="outlined" fullWidth>
          Schritt hinzufügen
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant="contained"
          onClick={onCreate}
          disabled={isCreating}
          startIcon={isCreating ? <CircularProgress size={16} /> : <SaveTemplateIcon />}
        >
          {isCreating ? 'Speichert...' : 'Vorlage speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateVorlageDialog;
